/**
 * StandardBreadcrumbs Component
 *
 * Unified breadcrumb navigation component.
 * Used across all applications for consistent navigation UX.
 *
 * @example
 * <StandardBreadcrumbs
 *   items={[
 *     { label: 'Home', href: '/' },
 *     { label: 'Products', href: '/products' },
 *     { label: 'Product Details' }
 *   ]}
 * />
 */

import React from 'react';
import { ChevronRight, Home } from 'lucide-react';

export interface BreadcrumbItem {
  label: string;
  href?: string;
  icon?: React.ReactNode;
  onClick?: () => void;
}

export interface StandardBreadcrumbsProps {
  items: BreadcrumbItem[];
  separator?: React.ReactNode;
  showHomeIcon?: boolean;
  maxItems?: number;
}

export const StandardBreadcrumbs: React.FC<StandardBreadcrumbsProps> = ({
  items,
  separator = <ChevronRight size={16} />,
  showHomeIcon = true,
  maxItems
}) => {
  // Truncate items if maxItems is set
  const displayItems = maxItems && items.length > maxItems
    ? [
        items[0],
        { label: '...', onClick: undefined },
        ...items.slice(-(maxItems - 2))
      ]
    : items;

  return (
    <nav aria-label="Breadcrumb" className="flex">
      <ol className="flex items-center gap-2">
        {displayItems.map((item, index) => {
          const isLast = index === displayItems.length - 1;
          const isFirst = index === 0;

          return (
            <li key={index} className="flex items-center gap-2">
              {/* Breadcrumb Item */}
              {item.href || item.onClick ? (
                <a
                  href={item.href}
                  onClick={(e) => {
                    if (item.onClick) {
                      e.preventDefault();
                      item.onClick();
                    }
                  }}
                  className={`
                    flex items-center gap-1.5 text-sm
                    ${isLast
                      ? 'text-gray-900 dark:text-gray-100 font-medium cursor-default'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors'
                    }
                  `}
                  aria-current={isLast ? 'page' : undefined}
                >
                  {isFirst && showHomeIcon && item.label === 'Home' ? (
                    <Home size={16} />
                  ) : item.icon ? (
                    item.icon
                  ) : null}
                  <span>{item.label}</span>
                </a>
              ) : (
                <span
                  className={`
                    flex items-center gap-1.5 text-sm
                    ${isLast
                      ? 'text-gray-900 dark:text-gray-100 font-medium'
                      : 'text-gray-600 dark:text-gray-400'
                    }
                  `}
                  aria-current={isLast ? 'page' : undefined}
                >
                  {isFirst && showHomeIcon && item.label === 'Home' ? (
                    <Home size={16} />
                  ) : item.icon ? (
                    item.icon
                  ) : null}
                  <span>{item.label}</span>
                </span>
              )}

              {/* Separator */}
              {!isLast && (
                <span className="text-gray-400 dark:text-gray-600" aria-hidden="true">
                  {separator}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};
